import * as React from 'react';
import { ChangeEvent, FC, useEffect, useState } from 'react';

import { useConfirm } from 'material-ui-confirm';
import { useQueryClient } from '@tanstack/react-query';
import { Edit, Package, Trash2 } from 'react-feather';
import { NavLink, useLocation } from 'react-router-dom';

import {
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import {
  useGetOrganizations,
  useDeleteOrganization,
  useUpdateOrgStatus,
} from '../../../api-http/organizations';
import { Nullable } from '../../../types';
import { AlertBar } from '../../../components/AlertBar';
import { useAuth } from '../../../context/auth-context';
import { OrderEnum, SortEnum } from '../../../types/pagination.types';
import { displayDate } from '../../../util/dateTime';
import { notifyMsg } from '../../../configs';
import { useSnack } from '../../../util/useSnack';
import { useTable } from '../../../util/useTable';
import { BaseOrganization } from '../../../types/organization.types';
import { UpdateOrganizationModal } from './components/UpdateOrganizationModal';
import { DialogModal } from '../../../components';
import { SetPackagesModal } from './components/SetPackagesModal';
import { UserRole } from '../../../types/user.types';
import { useIsUserRole } from '../../../util/useIsUserRole';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../../components/TableTheme';
import { managementTableSx } from '../../../components/ManagementPageLayout';

interface OrganizationsTableProp {
  selectedOrgName: Nullable<string>;
}

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

export const OrganizationsTable: FC<OrganizationsTableProp> = ({
  selectedOrgName,
}) => {
  const [open, setOpen] = useState(false);
  const disableSetPackage = useIsUserRole([
    UserRole.user,
    UserRole.layoutEditor,
    UserRole.dataEditor,
  ]);
  const { currentUser, switchedOrgId, setSwitchedOrgId } = useAuth();
  const queryClient = useQueryClient();
  const { showSuccessSnack, showErrSnack } = useSnack();
  const confirm = useConfirm();
  const { sortOnClick, pagingParams, setPage, tableProps } = useTable(true);
  const { sortby, order } = pagingParams;
  const location = useLocation();

  const [orgUpdateModal, setOrgUpdateModal] = useState(false);
  const [activeOrg, setActiveOrg] = useState<Nullable<BaseOrganization>>(null);

  useEffect(() => {
    if (selectedOrgName) {
      setPage(0);
    }
  }, [selectedOrgName]);

  const {
    data: paginateData,
    isLoading,
    error,
    refetch,
  } = useGetOrganizations(
    { ...pagingParams, search: selectedOrgName || '' },
    { onError: (err) => showErrSnack(err) },
  );

  const {
    mutate: updateStatus,
    isLoading: statusIsLoading,
    error: statusError,
  } = useUpdateOrgStatus({
    onSuccess(response) {
      if (!response.status && switchedOrgId === response._id) {
        setSwitchedOrgId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['organization/all'] });
      showSuccessSnack(
        response.status
          ? notifyMsg.ORGANIZATION_ENABLE_SUCCESS
          : notifyMsg.ORGANIZATION_DISABLE_SUCCESS,
      );
      refetch();
    },
    onError: (err) => showErrSnack(err),
  });

  const {
    mutate: deleteOrganization,
    isLoading: deleteIsLoading,
    error: deleteError,
  } = useDeleteOrganization({
    onSuccess: ({ organizationId }) => {
      if (switchedOrgId === organizationId) {
        setSwitchedOrgId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['organization/all'] });
      showSuccessSnack(notifyMsg.ORGANIZATION_DELETE_SUCCESS);
      refetch();
    },
    onError: (err) => showErrSnack(err),
  });

  const onOrgUpdate = (organization: BaseOrganization) => {
    setActiveOrg(organization);
    setOrgUpdateModal(true);
  };

  const switchHandler = (event: ChangeEvent<HTMLInputElement>, orgId: string) =>
    updateStatus({ id: orgId || '', status: event.target.checked });

  const handleDelete = async (organization: BaseOrganization) => {
    try {
      await confirm({
        title: `Delete ${organization.name}?`,
        description:
          'This permanently deletes the organization and all of its KVS data, schedulers, assets, devices, forms, slides, presentations, users, and related records. This action cannot be undone.',
        confirmationText: 'Delete permanently',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });
      deleteOrganization({ id: organization._id });
    } catch (error) {}
  };

  return (
    <>
      {(statusIsLoading || deleteIsLoading || isLoading) && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg} />
      <AlertBar severity="error" msg={statusError?.msg} />
      <AlertBar severity="error" msg={deleteError?.msg} />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 960 }} aria-label="Organizations">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.name}
                  direction={sortby === SortEnum.name ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.name)}
                >
                  Organization name
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Pin code</StyledTableCell>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Created
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center">Status</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginateData?.data.map((organization) => {
              return (
                <TableRow key={organization._id} sx={{ height: 56 }}>
                  <TableCell component="th" scope="row">
                    <NavLink
                      to={`/admin/users/${organization._id}`}
                      state={{
                        search: location?.search,
                      }}
                    >
                      {organization?.name}
                    </NavLink>
                  </TableCell>
                  <TableCell>{organization?.email}</TableCell>
                  <TableCell>{organization?.pinCode}</TableCell>
                  <TableCell>{displayDate(organization?.updatedAt)}</TableCell>
                  <TableCell>{displayDate(organization?.createdAt)}</TableCell>
                  <TableCell align="center">
                    <Switch
                      id={organization?._id}
                      inputProps={{
                        'aria-label': `${
                          organization.status ? 'Disable' : 'Enable'
                        } ${organization.name}`,
                      }}
                      disabled={
                        currentUser?.organization?._id == organization?._id
                      }
                      checked={organization?.status}
                      onChange={(event) =>
                        switchHandler(event, organization._id)
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Select package">
                        <span>
                          <IconButton
                            aria-label={`Select package for ${organization.name}`}
                            onClick={() => {
                              setActiveOrg(organization);
                              setOpen(true);
                            }}
                            disabled={disableSetPackage}
                            sx={{ ...actionButtonSx, color: '#64748b' }}
                          >
                            <Package size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit organization">
                        <IconButton
                          aria-label={`Edit ${organization.name}`}
                          onClick={() => onOrgUpdate(organization)}
                          sx={{ ...actionButtonSx, color: '#2563eb' }}
                        >
                          <Edit size={15} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete organization">
                        <span>
                          <IconButton
                            aria-label={`Delete ${organization.name}`}
                            disabled={
                              currentUser?.organization?._id ===
                                organization._id || deleteIsLoading
                            }
                            onClick={() => handleDelete(organization)}
                            sx={{
                              ...actionButtonSx,
                              color: '#ef4444',
                              borderColor: '#fecaca',
                              bgcolor: '#fffafa',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={paginateData?.pagination.total === 0}
          count={paginateData?.pagination.total || 0}
        />
      </TableContainer>

      <UpdateOrganizationModal
        onClose={() => setOrgUpdateModal(false)}
        open={orgUpdateModal}
        onFinished={() => refetch()}
        organizationData={activeOrg}
      />

      <DialogModal
        title="Select package"
        onClose={() => setOpen(false)}
        open={open}
        width={500}
      >
        <SetPackagesModal
          organizationName={activeOrg?.name}
          onFinished={() => setOpen(false)}
        />
      </DialogModal>
    </>
  );
};
